import useDownloadResource from '@/features/shared/Hooks/download/useDownloadResource';
import { saveAs } from 'file-saver';
import { useCallback, useMemo } from 'react';
import { useDownloadBase64Resource } from './useDownloadBase64Resource';

interface UseDownloadPriceListHandlerProps {
  fileUrlSource: string;
  fileType: string;
  fileName: string;
  contentType: string;
  downloadProvider: DownloadResourceProviders;
}

export enum DownloadResourceProviders {
  base64 = 'base64',
  binary = 'binary',
}

export const useDownloadPriceListHandler = ({
  fileUrlSource,
  fileType,
  fileName,
  contentType,
  downloadProvider,
}: UseDownloadPriceListHandlerProps) => {
  const { downloadResource, loading, error } = useDownloadResource();
  const {
    downloadResource: base64DownloadResource,
    loading: loadingLatest,
    error: errorLatest,
  } = useDownloadBase64Resource();

  const downloadResourceBinary = useCallback(async () => {
    const blobFile = await downloadResource({
      url: fileUrlSource,
      payload: {
        method: fileType,
      },
    });
    if (!blobFile) return null;
    const blob = new Blob([blobFile], { type: contentType });
    saveAs(blob, fileName);
    return null;
  }, [contentType, downloadResource, fileName, fileType, fileUrlSource]);

  const downloadResourceBase64 = useCallback(async () => {
    const downloadResponse = await base64DownloadResource({
      url: `${fileUrlSource}/v2`,
      payload: {
        method: fileType,
      },
    });

    if (!downloadResponse) return null;
    saveAs(downloadResponse.blobFile, downloadResponse.filename);
    return {
      fileName: downloadResponse.filename,
    };
  }, [base64DownloadResource, fileType, fileUrlSource]);

  const download = useCallback(async () => {
    // Todo: Remove this logic after base64 downloader is stable
    const downloader =
      downloadProvider === DownloadResourceProviders.base64
        ? downloadResourceBase64
        : downloadResourceBinary;
    return downloader();
  }, [downloadResourceBinary, downloadProvider, downloadResourceBase64]);

  const isDownloading = useMemo(() => loading || loadingLatest, [loading, loadingLatest]);
  const downloadError = useMemo(() => error || errorLatest, [error, errorLatest]);

  return { download, isDownloading, downloadError };
};
